import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DiyLayout from "../../components/Layout/DiyLayout";
import { useDiySelfClient } from "../../hooks/useDiySelfClient";
import { aiApi } from "../../api/aiApi";
import { disputesApi } from "../../api/disputesApi";
import { scoresApi } from "../../api/scoresApi";
import { documentsApi } from "../../api/documentsApi";
import type { ClientDocument } from "../../api/documentsApi";
import type { CreditAnalysis, DisputeItem } from "../../types";
import { CardGridSkeleton, Skeleton } from "../../components/ui/Skeleton";
import Spinner from "../../components/ui/Spinner";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import { AnimatedCounter } from "../../components/diy/AnimatedCounter";
import { normalizeDiyDisputeBucket, getDisputeBureaus } from "./diyShared";
import "../../styles/diy-animations.css";

function parseSteps(raw: string | undefined): string[] {
  if (!raw?.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((x) => (typeof x === "string" ? x : String(x))).filter(Boolean);
    }
  } catch {
    /* ignore */
  }
  return [];
}

function isCreditReportDoc(d: ClientDocument): boolean {
  return d.category === "CreditReport" || d.category === "credit_report";
}

function itemBorderColor(d: DisputeItem): string {
  const b = normalizeDiyDisputeBucket(d.status);
  if (b === "resolved") {
    return "#10B981";
  }
  const t = `${d.accountType} ${d.disputeReason}`.toLowerCase();
  if (/error|inaccurate|false|fraud|incorrect/.test(t)) {
    return "#EF4444";
  }
  return "#F59E0B";
}

function itemRecommendation(d: DisputeItem): { text: string; color: string } {
  const b = normalizeDiyDisputeBucket(d.status);
  if (b === "resolved") {
    return { text: "Clean", color: "#10B981" };
  }
  const t = `${d.accountType} ${d.disputeReason}`.toLowerCase();
  if (/error|inaccurate|false|fraud|incorrect/.test(t)) {
    return { text: "Error Detected", color: "#EF4444" };
  }
  return { text: "Recommend Dispute", color: "#F59E0B" };
}

const DiyMyReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { client, clientId, loading: clientLoading, error: clientError, reload } = useDiySelfClient();
  const [analysis, setAnalysis] = useState<CreditAnalysis | null>(null);
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [history, setHistory] = useState<{ period: string; bureau: string; score: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [aiRunning, setAiRunning] = useState(false);
  const [aiError, setAiError] = useState("");

  const load = useCallback(async () => {
    if (!clientId || !client) {
      setAnalysis(null);
      setDisputes([]);
      setDocuments([]);
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [a, d, h, docs] = await Promise.all([
        aiApi.getLatestAnalysis(clientId).catch(() => null),
        disputesApi.getAllForClient(clientId).catch(() => []),
        scoresApi.getHistory(clientId, 365 * 3).catch(() => []),
        documentsApi.list(clientId).catch(() => []),
      ]);
      setAnalysis(a);
      setDisputes(d);
      setHistory(h);
      setDocuments(docs);
    } catch {
      setError("Could not load report data.");
    } finally {
      setLoading(false);
    }
  }, [client, clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartData = useMemo(() => {
    const byPeriod = new Map<string, Record<string, number | string>>();
    for (const p of history) {
      const key = p.period;
      if (!byPeriod.has(key)) {
        byPeriod.set(key, { period: key });
      }
      const row = byPeriod.get(key)!;
      const b = p.bureau.toLowerCase();
      if (b.includes("equifax")) {
        row.Equifax = p.score;
      } else if (b.includes("experian")) {
        row.Experian = p.score;
      } else {
        row.TransUnion = p.score;
      }
    }
    return Array.from(byPeriod.values()).sort((a, b) => String(a.period).localeCompare(String(b.period)));
  }, [history]);

  const actionSteps = useMemo(() => parseSteps(analysis?.actionPlanStepsJson), [analysis?.actionPlanStepsJson]);

  const creditReportDocs = useMemo(() => documents.filter(isCreditReportDoc), [documents]);
  const hasCreditReport = creditReportDocs.length > 0;

  const errorItemsCount = useMemo(
    () =>
      disputes.filter((d) => /error|inaccurate|false|fraud|incorrect/i.test(`${d.accountType} ${d.disputeReason}`))
        .length,
    [disputes]
  );

  const runAnalysis = async () => {
    if (!clientId || !client) {
      return;
    }
    setAiRunning(true);
    setAiError("");
    try {
      const list = await disputesApi.getAllForClient(clientId);
      await aiApi.analyzeCreditReport({
        clientId,
        creditReportJson: JSON.stringify({
          generatedAt: new Date().toISOString(),
          source: "DiyMyReportPage",
          clientProfile: {
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.email,
            status: client.status,
            scores: {
              equifax: client.equifaxScore,
              experian: client.experianScore,
              transUnion: client.transUnionScore,
            },
          },
          disputes: list.map((x) => ({
            creditor: x.creditor,
            status: x.status,
            accountType: x.accountType,
            balance: x.balance,
            disputeReason: x.disputeReason,
            priorityRank: x.priorityRank,
            estimatedScoreImpact: x.estimatedScoreImpact,
            bureaus: {
              equifax: x.equifaxDisputed,
              experian: x.experianDisputed,
              transUnion: x.transUnionDisputed,
            },
          })),
        }),
      });
      const latest = await aiApi.getLatestAnalysis(clientId);
      setAnalysis(latest);
    } catch {
      setAiError("AI analysis failed. Try again in a moment.");
    } finally {
      setAiRunning(false);
    }
  };

  const glassCard = {
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.07)",
  };

  return (
    <DiyLayout>
      <div className="mx-auto max-w-lg md:max-w-2xl">
        <header className="mb-6">
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, color: "#F1F5F9" }}>
            My Credit Report
          </h1>
          <p className="mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#94A3B8" }}>
            AI analysis of your uploaded report
          </p>
        </header>

        {clientError ? (
          <p className="text-sm" style={{ color: "#FECACA", fontFamily: "'DM Sans', sans-serif" }}>
            {clientError}{" "}
            <button type="button" className="underline" style={{ color: "#0EA5E9" }} onClick={() => void reload()}>
              Retry
            </button>
          </p>
        ) : null}

        {clientLoading || (loading && client) ? (
          <CardGridSkeleton cards={3} />
        ) : client ? (
          <>
            {!hasCreditReport ? (
              <ScrollReveal>
                <div className="flex flex-col items-center rounded-2xl px-6 py-14 text-center" style={glassCard}>
                  <div className="mb-4 h-16 w-16 rounded-2xl" style={{ background: "rgba(16,185,129,0.1)" }} />
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#94A3B8" }}>
                    Upload your credit report to get started
                  </p>
                  <Link
                    to="/diy/documents"
                    className="mt-6 inline-block rounded-xl px-6 py-3 text-center font-semibold text-white no-underline transition-transform hover:scale-[1.02]"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                      boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
                    }}
                  >
                    Upload report
                  </Link>
                </div>
              </ScrollReveal>
            ) : (
              <>
                <ScrollReveal>
                  <div className="grid grid-cols-3 gap-3">
                    {(
                      [
                        { label: "Items Found", end: disputes.length, color: "#F1F5F9" },
                        {
                          label: "Disputable",
                          end: disputes.filter((d) => normalizeDiyDisputeBucket(d.status) !== "resolved").length,
                          color: "#F59E0B",
                        },
                        { label: "Errors", end: errorItemsCount, color: "#EF4444" },
                      ] as const
                    ).map((s) => (
                      <div key={s.label} className="rounded-xl p-4 text-center" style={glassCard}>
                        <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 28, color: s.color }}>
                          <AnimatedCounter end={s.end} duration={1000} />
                        </p>
                        <p
                          className="mt-1"
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#94A3B8",
                          }}
                        >
                          {s.label}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <section className="mt-8 rounded-2xl p-5" style={glassCard}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#F1F5F9" }}>
                      AI analysis
                    </h2>
                    {!analysis && (
                      <button
                        type="button"
                        onClick={() => void runAnalysis()}
                        disabled={aiRunning}
                        className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-0 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                          boxShadow: "0 4px 15px rgba(16,185,129,0.25)",
                        }}
                      >
                        {aiRunning ? <Spinner className="h-4 w-4" /> : null}
                        {aiRunning ? "Running…" : "Run AI Analysis"}
                      </button>
                    )}
                  </div>
                  {aiError ? <p className="mt-2 text-sm" style={{ color: "#FECACA" }}>{aiError}</p> : null}
                  {error ? <p className="mt-2 text-sm" style={{ color: "#FECACA" }}>{error}</p> : null}

                  {analysis ? (
                    <div className="mt-4 space-y-3 text-sm" style={{ color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>
                      <p>
                        <span style={{ color: "#64748B" }}>Priority:</span>{" "}
                        <span style={{ color: "#0EA5E9", fontWeight: 600 }}>{analysis.priorityLevel}</span>
                      </p>
                      <p>
                        <span style={{ color: "#64748B" }}>Score improvement estimate:</span>{" "}
                        <span style={{ color: "#E2E8F0" }}>
                          {analysis.estimatedScoreImprovementLow}–{analysis.estimatedScoreImprovementHigh} pts
                        </span>
                      </p>
                      <p>
                        <span style={{ color: "#64748B" }}>Headline:</span>{" "}
                        <span style={{ color: "#E2E8F0" }}>{analysis.actionPlanHeadline}</span>
                      </p>
                      {analysis.analystNote?.trim() ? (
                        <div>
                          <span style={{ color: "#64748B" }}>Analyst notes:</span>
                          <p className="mt-1 whitespace-pre-wrap" style={{ color: "#CBD5E1" }}>
                            {analysis.analystNote}
                          </p>
                        </div>
                      ) : null}
                      {actionSteps.length > 0 ? (
                        <div>
                          <span style={{ color: "#64748B" }}>Action steps:</span>
                          <ol className="mt-2 list-decimal space-y-1 pl-5" style={{ color: "#CBD5E1" }}>
                            {actionSteps.map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ol>
                        </div>
                      ) : null}
                      <p className="text-xs" style={{ color: "#64748B" }}>
                        Last run: {new Date(analysis.createdAt).toLocaleString()}
                      </p>
                      <button
                        type="button"
                        onClick={() => void runAnalysis()}
                        disabled={aiRunning}
                        className="rounded-xl border px-3 py-2 text-xs transition-colors"
                        style={{
                          borderColor: "rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "#E2E8F0",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        Refresh analysis
                      </button>
                    </div>
                  ) : !loading ? (
                    <p className="mt-4 text-sm" style={{ color: "#64748B", fontFamily: "'DM Sans', sans-serif" }}>
                      No analysis yet. Run AI when you have disputes on file.
                    </p>
                  ) : null}
                </section>

                <p
                  className="mb-4 mt-10"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: 10,
                    color: "#10B981",
                    letterSpacing: 2,
                  }}
                >
                  REPORT ITEMS
                </p>

                {disputes.length === 0 ? (
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#64748B" }}>
                    No parsed items yet. Parse your credit report from Documents.
                  </p>
                ) : (
                  disputes.map((d, i) => {
                    const border = itemBorderColor(d);
                    const rec = itemRecommendation(d);
                    const bureaus = getDisputeBureaus(d);
                    return (
                      <ScrollReveal key={d.id} delay={i * 0.05}>
                        <div
                          className="card-hover mb-3 rounded-xl p-4"
                          style={{
                            ...glassCard,
                            borderLeft: `3px solid ${border}`,
                          }}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 14, color: "#F1F5F9" }}>
                                {d.creditor}
                              </p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {bureaus.map((b) => (
                                  <span
                                    key={b}
                                    className="rounded-full px-2 py-0.5"
                                    style={{
                                      fontFamily: "'DM Sans', sans-serif",
                                      fontWeight: 500,
                                      fontSize: 10,
                                      background: "rgba(14,165,233,0.12)",
                                      color: "#0EA5E9",
                                    }}
                                  >
                                    {b}
                                  </span>
                                ))}
                              </div>
                            </div>
                            {normalizeDiyDisputeBucket(d.status) !== "resolved" ? (
                              <button
                                type="button"
                                onClick={() => navigate("/diy/my-disputes")}
                                className="shrink-0 cursor-pointer rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors"
                                style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  borderColor: "rgba(255,255,255,0.12)",
                                  background: "rgba(255,255,255,0.06)",
                                  color: "#E2E8F0",
                                }}
                              >
                                Dispute This
                              </button>
                            ) : null}
                          </div>
                          <p className="mt-2 text-xs" style={{ fontFamily: "'DM Sans', sans-serif", color: "#94A3B8" }}>
                            {d.accountType} · Balance ${d.balance?.toLocaleString?.() ?? d.balance}
                          </p>
                          <p className="mt-2 text-xs font-medium" style={{ fontFamily: "'DM Sans', sans-serif", color: rec.color }}>
                            {rec.text}
                          </p>
                        </div>
                      </ScrollReveal>
                    );
                  })
                )}

                <section className="mt-10 rounded-2xl p-5" style={glassCard}>
                  <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#F1F5F9" }}>
                    Score history
                  </h2>
                  {history.length === 0 ? (
                    <p className="mt-4 text-sm" style={{ color: "#64748B", fontFamily: "'DM Sans', sans-serif" }}>
                      No history yet. Scores update when you apply a parsed report or add snapshots from the operator
                      score tools.
                    </p>
                  ) : (
                    <div className="mt-4 h-72 w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                          <XAxis dataKey="period" tick={{ fill: "#94A3B8", fontSize: 11 }} />
                          <YAxis domain={[300, 850]} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#0B1120",
                              border: "1px solid rgba(255,255,255,0.1)",
                              borderRadius: 12,
                            }}
                            labelStyle={{ color: "#E2E8F0" }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="Equifax" stroke="#0EA5E9" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="Experian" stroke="#8B5CF6" dot={false} strokeWidth={2} />
                          <Line type="monotone" dataKey="TransUnion" stroke="#10B981" dot={false} strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </section>
              </>
            )}
          </>
        ) : (
          !clientLoading && <Skeleton className="h-40 rounded-xl" />
        )}
      </div>
    </DiyLayout>
  );
};

export default DiyMyReportPage;
